<template>
  <div class="ayuda-mg ticket-mg" role="dialog" aria-label="Levantar un ticket de soporte">
    <div class="ticket-mg__caja">
      <header class="ticket-mg__cabecera">
        <div>
          <div class="ticket-mg__ceja">Ticket de soporte</div>
          <div class="ticket-mg__titulo">
            {{ captura ? 'Señala dónde está el problema' : 'Cuéntanos qué pasó' }}
          </div>
        </div>
        <button type="button" class="ticket-mg__cerrar" title="Cancelar" @click="$emit('cancelar')">
          <i class="iconoir-cancel"></i>
        </button>
      </header>

      <div v-if="captura" class="ticket-mg__lienzo-caja">
        <p class="ticket-mg__instruccion">
          Arrastra sobre la captura para encerrar en rojo lo que falla. Puedes marcar varias cosas.
        </p>
        <canvas
          ref="lienzo"
          class="ticket-mg__lienzo"
          @mousedown="empezar"
          @mousemove="mover"
          @mouseup="terminar"
          @mouseleave="terminar"
        ></canvas>
        <div class="ticket-mg__herramientas">
          <button type="button" class="ticket-mg__boton-sec" :disabled="!marcas.length" @click="deshacer">
            <i class="iconoir-undo"></i> Deshacer
          </button>
          <span class="ticket-mg__nota">{{ marcas.length }} marca{{ marcas.length === 1 ? '' : 's' }}</span>
        </div>
      </div>
      <p v-else class="ticket-mg__instruccion">
        No se pudo tomar la captura de la pantalla. El ticket va sin ella; describe con detalle lo que ves.
      </p>

      <label class="ticket-mg__etiqueta" for="ticket-mg-descripcion">¿Qué pasó? ¿Qué esperabas que pasara?</label>
      <textarea
        id="ticket-mg-descripcion"
        v-model="descripcion"
        class="ticket-mg__campo"
        rows="3"
        maxlength="5000"
        placeholder="Por ejemplo: le doy clic a la celda de Estado y no se abre la lista."
      ></textarea>
      <p v-if="error" class="ticket-mg__error">{{ error }}</p>

      <footer class="ticket-mg__pie">
        <button type="button" class="ticket-mg__boton-sec" :disabled="enviando" @click="$emit('cancelar')">
          Cancelar
        </button>
        <button
          type="button"
          class="ticket-mg__boton"
          :disabled="enviando || descripcion.trim().length < 5"
          @click="enviar"
        >
          {{ enviando ? 'Enviando…' : 'Enviar ticket' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import AyudaService from '@extras_mg/services/ayuda'

const ROJO = '#e2231a'

/**
 * La captura de la página con lo que la persona encierra en rojo, más su
 * descripción y la conversación con el asistente. Las marcas se guardan en
 * coordenadas de la imagen (no de la pantalla) y se pintan sobre ella al
 * enviar: lo que llega a GitHub es la captura ya señalada.
 *
 * Lleva la clase "ayuda-mg" para que una segunda captura no la fotografíe.
 */
export default {
  name: 'TicketSoporteMG',
  props: {
    captura: { type: String, required: true },
    conversacion: { type: Array, required: true },
  },
  emits: ['cancelar', 'enviado'],
  data() {
    return { descripcion: '', marcas: [], actual: null, imagen: null, enviando: false, error: '' }
  },
  mounted() {
    if (!this.captura) return
    const imagen = new Image()
    imagen.onload = () => {
      this.imagen = imagen
      const lienzo = this.$refs.lienzo
      lienzo.width = imagen.naturalWidth
      lienzo.height = imagen.naturalHeight
      this.pintar()
    }
    imagen.src = this.captura
  },
  methods: {
    punto(evento) {
      const caja = this.$refs.lienzo.getBoundingClientRect()
      const escala = this.$refs.lienzo.width / caja.width
      return { x: (evento.clientX - caja.left) * escala, y: (evento.clientY - caja.top) * escala }
    },
    empezar(evento) {
      const p = this.punto(evento)
      this.actual = { x: p.x, y: p.y, w: 0, h: 0 }
    },
    mover(evento) {
      if (!this.actual) return
      const p = this.punto(evento)
      this.actual.w = p.x - this.actual.x
      this.actual.h = p.y - this.actual.y
      this.pintar()
    },
    terminar() {
      if (this.actual && Math.abs(this.actual.w) > 6 && Math.abs(this.actual.h) > 6) {
        this.marcas.push(this.actual)
      }
      this.actual = null
      this.pintar()
    },
    deshacer() {
      this.marcas.pop()
      this.pintar()
    },
    pintar(lienzo = this.$refs.lienzo) {
      if (!this.imagen || !lienzo) return
      const ctx = lienzo.getContext('2d')
      ctx.drawImage(this.imagen, 0, 0)
      ctx.lineWidth = Math.max(3, lienzo.width / 360)
      ctx.strokeStyle = ROJO
      for (const m of [...this.marcas, this.actual].filter(Boolean)) {
        ctx.strokeRect(m.x, m.y, m.w, m.h)
      }
    },
    async enviar() {
      this.enviando = true
      this.error = ''
      const formulario = new FormData()
      formulario.append('descripcion', this.descripcion.trim())
      formulario.append(
        'conversacion',
        JSON.stringify(
          this.conversacion.filter((m) => !m.sinHistorial).map(({ rol, texto }) => ({ rol, texto }))
        )
      )
      formulario.append('pagina', window.location.href)
      if (this.imagen) {
        this.actual = null
        this.pintar()
        const png = await new Promise((resolve) => this.$refs.lienzo.toBlob(resolve, 'image/png'))
        if (png) formulario.append('imagen', png, 'captura.png')
      }
      try {
        const { data } = await AyudaService(this.$client).levantarTicket(formulario)
        this.$emit('enviado', data.numero)
      } catch (e) {
        this.error =
          e.response?.data?.detail ||
          'No se pudo enviar el ticket. Escríbele a Victor por WhatsApp o a vvazquez@mandalagroup.com.'
      } finally {
        this.enviando = false
      }
    },
  },
}
</script>

<style lang="scss">
.ticket-mg {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(14, 14, 15, 0.6);
}

.ticket-mg__caja {
  width: min(1040px, 100%);
  max-height: 100%;
  overflow-y: auto;
  padding: 0 0 18px;
  border-radius: 12px;
  background: var(--mg-fondo, #ffffff);
  color: var(--mg-texto, #323338);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.3);
}

.ticket-mg__cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  color: #ffffff;
  background: #0e0e0f;
}

.ticket-mg__ceja {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #9a9a9a;
}

.ticket-mg__titulo {
  font-size: 18px;
  font-weight: 300;
}

.ticket-mg__cerrar {
  border: 0;
  background: transparent;
  color: #ffffff;
  font-size: 22px;
  cursor: pointer;
}

.ticket-mg__lienzo-caja,
.ticket-mg__etiqueta,
.ticket-mg__campo,
.ticket-mg__error,
.ticket-mg__pie,
.ticket-mg > .ticket-mg__caja > .ticket-mg__instruccion {
  margin-left: 20px;
  margin-right: 20px;
}

.ticket-mg__instruccion {
  margin-top: 14px;
  margin-bottom: 8px;
  font-size: 13px;
}

.ticket-mg__lienzo {
  display: block;
  width: 100%;
  max-height: 58vh;
  object-fit: contain;
  border: 1px solid var(--mg-borde, #d0d4e4);
  border-radius: 6px;
  cursor: crosshair;
}

.ticket-mg__herramientas {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}

.ticket-mg__nota {
  font-size: 12px;
  opacity: 0.7;
}

.ticket-mg__etiqueta {
  display: block;
  margin-top: 16px;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
}

.ticket-mg__campo {
  display: block;
  width: calc(100% - 40px);
  resize: vertical;
  padding: 9px 11px;
  border: 1px solid var(--mg-borde, #d0d4e4);
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  color: inherit;
  background: transparent;
}

.ticket-mg__error {
  margin-top: 8px;
  font-size: 13px;
  color: #e2231a;
}

.ticket-mg__pie {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.ticket-mg__boton,
.ticket-mg__boton-sec {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ticket-mg__boton {
  border: 0;
  color: #ffffff;
  background: #0e0e0f;
}

.ticket-mg__boton-sec {
  border: 1px solid var(--mg-borde, #d0d4e4);
  color: inherit;
  background: transparent;
}

.ticket-mg__boton:disabled,
.ticket-mg__boton-sec:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>
